/**
 * GRELIA PAGE TRANSITIONS - True Apple Style
 * Brand Colors: Blue #191d38, Yellow #FDB91A, White #ffffff
 * 
 * Flow:
 * 1. Click → Fetch new page in background (no visual change)
 * 2. When loaded → Fade out current (250ms)
 * 3. Swap content
 * 4. Fade in new (250ms)
 * 
 * Total transition: exactly 500ms, never breaks on slow internet
 */

(function () {
  'use strict';

  const FADE_OUT = 250;
  const FADE_IN = 250;
  const BLUE = '#191d38';

  const EXCLUDE = [
    '[target="_blank"]',
    '[download]',
    '[href^="mailto:"]',
    '[href^="tel:"]',
    '[href^="#"]',
    '[href*="/account"]',
    '[href*="/pages/"]',
    // Shopify-managed flows: must hard-load so form actions, session
    // checks, and CSRF tokens re-initialize properly.
    '[href^="/checkout"]',
    '[href*="/checkouts/"]',
    '[href^="/cart"]',
    '.no-transition'
  ];

  let isTransitioning = false;
  let controller = null;
  // Remember scroll positions per history-state key so back/forward
  // returns the user to where they were, not to the top of the page.
  const scrollPositions = new Map();
  // Opt out of the browser's automatic restoration — we do it ourselves
  // because we swap <main> after the back nav.
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // Base styles
  const baseStyles = document.createElement('style');
  baseStyles.textContent = `
    html, body {
      background-color: ${BLUE} !important;
    }
    
    main {
      opacity: 1;
    }
    
    main.fading-out {
      opacity: 0;
      transition: opacity ${FADE_OUT}ms ease-out;
    }
    
    main.fading-in {
      opacity: 0;
      transition: opacity ${FADE_IN}ms ease-in;
    }
  `;
  document.head.appendChild(baseStyles);

  function shouldIntercept(link) {
    if (link.origin !== window.location.origin) return false;
    const targetUrl = new URL(link.href);
    const targetPath = targetUrl.pathname;
    const currentPath = window.location.pathname;

    // Home uses a fixed hero stage and inline section styles.
    // Hard load it instead of hot-swapping through the SPA layer.
    if (targetPath === '/' || currentPath === '/') return false;

    // Same-page hash links (e.g. /products/foo#section while on /products/foo)
    // should be handled by the browser as native anchor scrolls — never
    // intercept them as SPA navigations or the hash gets lost.
    if (targetUrl.hash && targetPath === currentPath && targetUrl.search === window.location.search) {
      return false;
    }

    for (const sel of EXCLUDE) {
      if (link.matches(sel)) return false;
    }
    return true;
  }

  async function fetchPage(url) {
    if (controller) controller.abort();
    controller = new AbortController();
    const res = await fetch(url, { signal: controller.signal });

    // NUCLEAR FIX: Detect Redirects (especially to Cart/Login)
    // If the fetched URL doesn't match the requested URL (ignoring trailing slashes),
    // it means Shopify redirected us. We must hard load to follow it properly.
    if (res.redirected || (res.url && !res.url.includes(url))) {
      console.warn('Redirect detected in SPA fetch. Hard loading...');
      throw new Error('Redirect detected');
    }

    if (!res.ok) throw new Error('Fetch failed');
    return res.text();
  }

  function parseHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return {
      title: doc.title,
      main: doc.querySelector('main'),
      footer: doc.querySelector('footer.grella-footer, footer'),
      footerStyle: doc.querySelector('footer.grella-footer + style, footer + style')
    };
  }

  function cleanupDetachedPdpUi() {
    if (typeof window.teardownProductStickyCart === 'function') {
      window.teardownProductStickyCart();
      return;
    }

    const stickyCart = document.getElementById('mobileCartBar');
    const drawerBackdrop = document.getElementById('drawerBackdrop');

    if (stickyCart) stickyCart.remove();
    if (drawerBackdrop) drawerBackdrop.remove();

    window.drawerExpanded = false;
    window.stickyCartQty = 0;
  }

  // Soft hard-navigate: fade <main> out using the same timing as the SPA
  // transition, THEN trigger a real browser navigation. This is for cases
  // where we can't hot-swap (home page, popstate to home) — without it,
  // the user gets a jarring instant white flash, breaking the otherwise
  // smooth transition feel.
  function softNavigate(url) {
    const m = document.querySelector('main');
    if (!m) { window.location.href = url; return; }
    // Tear off the .page-ready class so the critical-css opacity rules
    // are the only thing controlling the fade (matches first-load timing).
    document.body.classList.remove('page-ready');
    m.style.transition = `opacity ${FADE_OUT}ms ease-out`;
    m.style.opacity = '0';
    setTimeout(() => { window.location.href = url; }, FADE_OUT);
  }

  async function navigateTo(url, opts = {}) {
    if (isTransitioning) return;

    const targetPath = new URL(url, window.location.origin).pathname;
    const currentPath = window.location.pathname;

    // Remember where we were on the OUTGOING page so we can restore it
    // when the user hits Back / Forward.
    scrollPositions.set(window.location.href, window.scrollY);
    // Cap the map so it can't grow unbounded across a long browsing session
    // (each back/forward only needs recent entries).
    if (scrollPositions.size > 50) { scrollPositions.delete(scrollPositions.keys().next().value); }

    // Home uses a fixed hero stage that doesn't survive innerHTML swap.
    // Use a soft hard-nav (fade-out + location change) so the visual
    // feel matches the SPA transition on every other route.
    //
    // Product pages get the same treatment: their behaviour (image
    // carousel, sticky add-to-cart, variant/price sync) lives in
    // assets/nuclear-product.js, which is loaded from the LAYOUT —
    // OUTSIDE <main>. An innerHTML swap of <main> never re-runs that
    // script, so an SPA-swapped PDP renders but is completely inert
    // (dead carousel, un-hoisted sticky cart, no add-to-cart). Soft-
    // navigating (fade-out + real load) keeps the transition feel while
    // loading the PDP's full script set. Matches /products/x and
    // /collections/x/products/y.
    if (targetPath === '/' || currentPath === '/' || targetPath.indexOf('/products/') !== -1) {
      softNavigate(url);
      return;
    }

    isTransitioning = true;

    const main = document.querySelector('main');

    try {
      // STEP 1: Fetch page completely first (no visual change yet)
      const html = await fetchPage(url);
      const newContent = parseHTML(html);

      // STEP 2: Now page is ready - start fade out
      main.style.transition = `opacity ${FADE_OUT}ms ease-out`;
      main.style.opacity = '0';

      await new Promise(r => setTimeout(r, FADE_OUT));

      // STEP 3: Swap content (invisible now)
      cleanupDetachedPdpUi();
      document.title = newContent.title;
      if (newContent.main) {
        main.innerHTML = newContent.main.innerHTML;
      }

      const currentFooter = document.querySelector('footer.grella-footer, footer');
      if (currentFooter && newContent.footer) {
        currentFooter.outerHTML = newContent.footer.outerHTML;
      }

      const currentFooterStyle = document.querySelector('footer.grella-footer + style, footer + style');
      if (currentFooterStyle && newContent.footerStyle) {
        currentFooterStyle.outerHTML = newContent.footerStyle.outerHTML;
      }

      // Only push a new history entry on forward navigations.
      // Back/forward (popstate) shouldn't add a new entry.
      if (!opts.isPopState) {
        window.history.pushState({ url }, newContent.title, url);
      }

      // Re-run scripts — preserve ALL attributes so type="application/json"
      // blobs aren't re-executed as JS, and async/defer/id/data-* survive.
      main.querySelectorAll('script').forEach(old => {
        const s = document.createElement('script');
        for (const attr of old.attributes) {
          s.setAttribute(attr.name, attr.value);
        }
        if (!old.src) s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
      });

      // Restore scroll position on back/forward, otherwise scroll to top.
      // Also honor `#hash` anchor in the URL on forward navigation.
      if (opts.isPopState) {
        const saved = scrollPositions.get(url);
        window.scrollTo(0, typeof saved === 'number' ? saved : 0);
      } else {
        const targetHash = new URL(url, window.location.origin).hash;
        if (targetHash) {
          const target = document.querySelector(targetHash);
          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'instant' in window ? 'instant' : 'auto' });
          } else {
            window.scrollTo(0, 0);
          }
        } else {
          window.scrollTo(0, 0);
        }
      }

      // Force paint at opacity 0
      void main.offsetHeight;

      // STEP 4: Fade in new content
      main.style.transition = `opacity ${FADE_IN}ms ease-in`;
      main.style.opacity = '1';

      await new Promise(r => setTimeout(r, FADE_IN));

      // Cleanup
      main.style.transition = '';
      main.style.opacity = '';

      // CRITICAL FIX: Re-initialize nuclear systems after SPA navigation
      // This ensures event delegation, wishlist, and cart systems work on new content
      // CRITICAL FIX: Re-initialize nuclear systems after SPA navigation
      // This ensures event delegation, wishlist, and cart systems work on new content
      if (window.nuclear) {
        window.nuclear.reinit();
      }

      window.dispatchEvent(new Event('pageTransitionComplete'));

    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Transition error:', err);
      main.style.opacity = '';
      main.style.transition = '';
      window.location.href = url;
    } finally {
      isTransitioning = false;
    }
  }

  function handleClick(e) {
    const link = e.target.closest('a');
    if (!link) return;
    if (!shouldIntercept(link)) return;
    e.preventDefault();
    navigateTo(link.href);
  }

  function handlePopState(e) {
    navigateTo(e.state?.url || window.location.href, { isPopState: true });
  }

  function init() {
    if (window.Shopify?.designMode) return;
    window.history.replaceState({ url: window.location.href }, document.title);
    document.addEventListener('click', handleClick);
    window.addEventListener('popstate', handlePopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
